import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { AnimatedContainer, ButtonLoadingForm, FormContainer } from '../styles';
import { StepIndicator } from '../components/StepIndicator';
import { ComponentUserRegisterProps, stepLabels } from '..';
import getValidationErrors from '../../../utils/getValidationErrors';
import InputCep from '../../../components/InputCep';
import Input from '../../../components/Input';
import { useCep } from '../../../hooks/cep';
import { LineInputThree, LineInputTwo } from './styles';
import InputSelect from '../../../components/InputSelect';
import getUF from '../../../utils/getUF';
import { Address as AddressData } from '../../../entities/Address';

export const Address: React.FC<ComponentUserRegisterProps> = ({ nextPage }) => {
  const formRef = useRef<FormHandles>(null);
  const [btnIsLoading, setBtnIsLoading] = useState<boolean>(false);

  const {
    getAddressData,
    addressData,
    setAddressData,
    loading,
    error,
  } = useCep();

  useEffect(() => {
    if (error) {
      formRef.current?.setFieldError('cep', error);
    }
  }, [error]);

  const handleInputCep = useCallback(
    async value => {
      if (value[value.length - 1] !== '_') {
        await getAddressData(formRef.current?.getFieldValue('cep'));
      }
    },
    [getAddressData],
  );

  useEffect(() => {
    if (addressData) {
      formRef.current?.setFieldValue('street', addressData.street);
      formRef.current?.setFieldValue('complement', addressData.complement);
      formRef.current?.setFieldValue('neighborhood', addressData.neighborhood);
      formRef.current?.setFieldValue('city', addressData.city);
      formRef.current?.setFieldValue('uf', addressData.uf);
      formRef.current?.setFieldError('cep', '');
    }
  }, [addressData]);

  const handleNext = useCallback(async () => {
    const data = formRef?.current?.getData() as AddressData;

    try {
      setBtnIsLoading(true);

      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        cep: Yup.string()
          .test(
            'length',
            'Digite um CEP válido',
            value => !!(!value || value.length === 9),
          )
          .required('O cep é obrigatório'),
        street: Yup.string().required('A rua é obrigatório'),
        number: Yup.string().required('O número da rua é obrigatório'),
        complement: Yup.string(),
        city: Yup.string().required('A cidade é obrigatória'),
        uf: Yup.string().required('O estado é obrigatório'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      setAddressData(prev => ({ ...prev, ...data }));
      nextPage();
    } catch (err) {
      setBtnIsLoading(false);

      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      }
    }
  }, [nextPage, setAddressData]);

  return (
    <AnimatedContainer>
      <StepIndicator activeIndex={2} titles={stepLabels} />

      <h1>Entrega</h1>

      <FormContainer>
        <p>
          Informe o seu endereço. Utilizaremos esse endereço para enviar a sua
          pulseira de monitoramento.
        </p>
        <Form ref={formRef} onSubmit={handleNext}>
          <InputCep
            isLoading={loading}
            mask="99999-999"
            onChange={e => handleInputCep(e.target.value)}
            name="cep"
            label="CEP"
            type="tel"
          />
          <Input name="street" label="Rua" />

          <LineInputTwo>
            <Input name="number" label="Número" type="number" />

            <Input name="complement" label="Complemento" />
          </LineInputTwo>

          <LineInputThree>
            <Input name="city" label="Cidade" />

            <InputSelect
              name="uf"
              label="Estado"
              options={getUF}
              placeholder="Selecione"
            />
          </LineInputThree>
        </Form>
      </FormContainer>
      <ButtonLoadingForm
        isLoading={btnIsLoading}
        btnType="darkBlue"
        onClick={handleNext}
      >
        Próximo
      </ButtonLoadingForm>
    </AnimatedContainer>
  );
};
